import React, { useState } from 'react';
import { X, Trophy, Sparkles, TrendingDown, ChevronRight, Printer } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt  = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtD = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** Returns 'YYYY-MM' for the given date (defaults to today). */
export function getMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Returns 'YYYY-MM' for the previous calendar month. */
export function getPrevMonthKey() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return getMonthKey(d);
}

// ── Grade metadata ────────────────────────────────────────────────────────────
const GRADE_META = {
  A:    { bg: 'from-emerald-900/70 to-emerald-800/30', border: 'border-emerald-600/60', text: 'text-emerald-300',  badge: 'bg-emerald-800/60 border-emerald-500/60',  headline: 'A month to be proud of.' },
  B:    { bg: 'from-blue-900/70 to-blue-800/30',       border: 'border-blue-600/60',    text: 'text-blue-300',     badge: 'bg-blue-800/60 border-blue-500/60',         headline: 'Solid progress this month.' },
  C:    { bg: 'from-amber-900/70 to-amber-800/30',     border: 'border-amber-600/60',   text: 'text-amber-300',    badge: 'bg-amber-800/60 border-amber-500/60',        headline: 'A fair month — room to grow.' },
  D:    { bg: 'from-orange-900/70 to-orange-800/30',   border: 'border-orange-600/60',  text: 'text-orange-300',   badge: 'bg-orange-800/60 border-orange-500/60',      headline: 'Tough month — but you tracked it.' },
  F:    { bg: 'from-rose-900/70 to-rose-800/30',       border: 'border-rose-600/60',    text: 'text-rose-300',     badge: 'bg-rose-800/60 border-rose-500/60',          headline: 'Hard month — awareness is step one.' },
  'N/A':{ bg: 'from-slate-800/70 to-slate-700/30',     border: 'border-slate-600/60',   text: 'text-slate-400',    badge: 'bg-slate-700/60 border-slate-500/60',        headline: 'Here\'s what you tracked.' },
};

// ── Stats computation ─────────────────────────────────────────────────────────
export function computePrevMonthStats(budgetData, monthKey) {
  const [yr, mo]    = monthKey.split('-').map(Number);
  const mStart      = new Date(yr, mo - 1, 1);
  const mEnd        = new Date(yr, mo, 0, 23, 59, 59, 999);
  const transactions = budgetData?.transactions || [];
  const categories   = budgetData?.categories   || [];
  const bills        = budgetData?.bills         || [];
  const snapshots    = budgetData?.monthlySnapshots || {};

  // Filter to just this month's transactions
  const monthTxns = transactions.filter(t => {
    const d = new Date(t.date + 'T12:00:00');
    return d >= mStart && d <= mEnd;
  });

  const expenses      = monthTxns.filter(t => t.kind === 'expense');
  const incomes       = monthTxns.filter(t => t.kind === 'income');
  const totalIncome   = incomes.reduce((s, t) => s + t.amount, 0);
  const totalSpent    = expenses.reduce((s, t) => s + t.amount, 0);
  const netSaved      = totalIncome - totalSpent;
  const savingsAmount = expenses
    .filter(t => t.type === 'Savings' || t.category === 'Savings' || t.category === 'Investments')
    .reduce((s, t) => s + t.amount, 0);
  const txnCount       = monthTxns.length;
  const savingsRate    = totalIncome > 0 ? ((totalIncome - totalSpent) / totalIncome) * 100 : null;
  const mostExpensive  = expenses.length > 0
    ? expenses.reduce((max, t) => t.amount > max.amount ? t : max, expenses[0])
    : null;

  // Bills paid on time this month
  const billsPaid  = bills.filter(b => b.paidMonths?.[monthKey] === true);
  const billsTotal = bills.length;

  // Snapshot-based under/over budget (most accurate — uses effective budget incl. rollovers)
  const snapshot        = snapshots[monthKey] || {};
  const underBudgetCats = Object.entries(snapshot)
    .filter(([, v]) => v.spent < v.effectiveBudget && v.effectiveBudget > 0)
    .map(([name, v]) => ({
      name,
      saved: v.effectiveBudget - v.spent,
      pct:   Math.round(((v.effectiveBudget - v.spent) / v.effectiveBudget) * 100),
    }))
    .sort((a, b) => b.saved - a.saved);

  const overBudgetCats = Object.entries(snapshot)
    .filter(([, v]) => v.spent > v.effectiveBudget && v.effectiveBudget > 0)
    .map(([name, v]) => ({
      name,
      over:  v.spent - v.effectiveBudget,
      pct:   Math.round(((v.spent - v.effectiveBudget) / v.effectiveBudget) * 100),
    }))
    .sort((a, b) => b.over - a.over);

  // Longest logging streak within this month
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const txnDates    = new Set(monthTxns.map(t => t.date));
  let maxStreak = 0, cur = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${yr}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (txnDates.has(ds)) { cur++; maxStreak = Math.max(maxStreak, cur); }
    else cur = 0;
  }

  // Grade (uses total budgeted from categories, not snapshot — more stable)
  const totalBudgeted = categories.reduce((s, c) => s + (c.budgeted || 0), 0);
  let grade = 'N/A';
  if (totalBudgeted > 0 && totalSpent > 0) {
    const ratio = totalSpent / totalBudgeted;
    if      (ratio <= 0.9  && (savingsRate === null || savingsRate >= 20)) grade = 'A';
    else if (ratio <= 1.0  && (savingsRate === null || savingsRate >= 10)) grade = 'B';
    else if (ratio <= 1.1)                                                  grade = 'C';
    else if (ratio <= 1.25)                                                 grade = 'D';
    else                                                                    grade = 'F';
  }

  const monthLabel = new Date(yr, mo - 1, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return {
    monthKey, monthLabel,
    totalIncome, totalSpent, netSaved, savingsAmount, savingsRate,
    txnCount, mostExpensive,
    billsPaid: billsPaid.length, billsTotal,
    underBudgetCats, overBudgetCats,
    maxStreak, grade,
  };
}

// ── Top-win headline logic ────────────────────────────────────────────────────
function pickTopWin(stats) {
  const { grade, underBudgetCats, billsPaid, billsTotal, maxStreak,
          netSaved, savingsAmount, txnCount, totalIncome } = stats;

  // Rank wins in priority order — pick first that applies
  if (grade === 'A')
    return { icon: '🏆', text: 'You crushed it — under budget AND saved 20%+ this month. That\'s rare.' };

  if (underBudgetCats.length > 0 && underBudgetCats[0].pct >= 15) {
    const c = underBudgetCats[0];
    return { icon: '🎯', text: `Staying under budget in ${c.name} by ${fmt(c.saved)} (${c.pct}% under target). Every dollar counts.` };
  }

  if (billsTotal > 0 && billsPaid === billsTotal)
    return { icon: '✅', text: `Paid all ${billsTotal} bill${billsTotal !== 1 ? 's' : ''} on time. No late fees, no stress — that's financial discipline.` };

  if (savingsAmount > 0)
    return { icon: '💰', text: `You moved ${fmt(savingsAmount)} into savings or investments. Your future self will thank you.` };

  if (netSaved > 0 && totalIncome > 0) {
    const rate = Math.round((netSaved / totalIncome) * 100);
    return { icon: '📈', text: `Spent less than you earned — ${fmt(netSaved)} left over (${rate}% savings rate). That's the whole game.` };
  }

  if (maxStreak >= 7)
    return { icon: '🔥', text: `Tracked expenses ${maxStreak} days in a row. Most people never build this habit — you did.` };

  if (txnCount >= 10)
    return { icon: '📝', text: `Logged ${txnCount} transactions this month. Awareness is the foundation of every financial win.` };

  return { icon: '💙', text: 'You showed up and tracked. That puts you ahead of 80% of people who never look at their finances at all.' };
}

// ── Opportunity framing (positive, never shaming) ────────────────────────────
function buildOpportunity(stats) {
  const { overBudgetCats, totalIncome, txnCount, billsTotal, billsPaid } = stats;

  if (overBudgetCats.length > 0) {
    const worst = overBudgetCats[0];
    const weeklyTarget = Math.round(worst.over / 4);
    return `${worst.name} ran ${fmt(worst.over)} over budget. What if you set aside ${fmt(weeklyTarget)} less per week there next month? Small adjustments compound fast.`;
  }

  if (billsTotal > 0 && billsPaid < billsTotal) {
    const missed = billsTotal - billsPaid;
    return `${missed} bill${missed !== 1 ? 's' : ''} went unpaid this month. Setting a reminder on the 1st could prevent late fees — sometimes it's just a notification away.`;
  }

  if (totalIncome === 0)
    return 'Add your monthly income in the Budget tab to unlock your savings rate and smarter goals next month.';

  if (txnCount < 5)
    return 'The more transactions you log, the clearer your picture gets. Try logging just one purchase per day next month — that\'s all it takes.';

  return null; // All good — no opportunity needed
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function MonthInReviewModal({ stats, onClose }) {
  const [printing, setPrinting] = useState(false);

  if (!stats) return null;

  const gc          = GRADE_META[stats.grade] || GRADE_META['N/A'];
  const topWin      = pickTopWin(stats);
  const opportunity = buildOpportunity(stats);

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => { window.print(); setPrinting(false); }, 100);
  };

  // ── Quick-stat cards
  const statCards = [
    stats.netSaved > 0 && {
      emoji: '💰', label: 'Net Saved',
      value: fmt(stats.netSaved),
      sub:   stats.savingsRate != null ? `${stats.savingsRate.toFixed(0)}% savings rate` : null,
      color: 'text-emerald-300',
    },
    stats.txnCount > 0 && {
      emoji: '📝', label: 'Transactions Logged',
      value: stats.txnCount,
      sub:   stats.maxStreak >= 3 ? `${stats.maxStreak}-day tracking streak` : null,
      color: 'text-blue-300',
    },
    stats.billsTotal > 0 && {
      emoji: stats.billsPaid === stats.billsTotal ? '✅' : '🔔',
      label: 'Bills Paid',
      value: `${stats.billsPaid} / ${stats.billsTotal}`,
      sub:   stats.billsPaid === stats.billsTotal ? 'All on time!' : `${stats.billsTotal - stats.billsPaid} missed`,
      color: stats.billsPaid === stats.billsTotal ? 'text-emerald-300' : 'text-amber-300',
    },
    stats.savingsAmount > 0 && {
      emoji: '📈', label: 'Put into Savings',
      value: fmt(stats.savingsAmount),
      sub:   'invested or saved',
      color: 'text-violet-300',
    },
    stats.mostExpensive && {
      emoji: '🛒', label: 'Biggest Purchase',
      value: fmtD(stats.mostExpensive.amount),
      sub:   stats.mostExpensive.description?.slice(0, 22) || 'single transaction',
      color: 'text-slate-300',
    },
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Gradient header ── */}
        <div className={`relative bg-gradient-to-br ${gc.bg} px-6 pt-6 pb-5 flex-shrink-0`}>
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/10 transition-colors"
          >
            <X size={15} />
          </button>

          {/* Month label + sparkle */}
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-yellow-400" />
            <span className="text-xs font-semibold text-slate-300 uppercase tracking-widest">
              Month in Review
            </span>
          </div>

          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl font-black text-slate-100 leading-tight">{stats.monthLabel}</h2>
              <p className={`text-sm mt-0.5 font-medium ${gc.text}`}>{gc.headline}</p>
            </div>
            {/* Grade badge */}
            <div className={`flex flex-col items-center justify-center w-16 h-16 rounded-xl border-2 flex-shrink-0 ${gc.badge}`}>
              <span className={`text-3xl font-black leading-none ${gc.text}`}>{stats.grade}</span>
              <span className="text-[10px] text-slate-400 mt-0.5 font-medium">
                {stats.grade === 'A' ? 'Excellent' : stats.grade === 'B' ? 'Good' :
                 stats.grade === 'C' ? 'Fair' : stats.grade === 'D' ? 'Poor' :
                 stats.grade === 'F' ? 'Over' : 'No Data'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Top win callout */}
          <div className="bg-gradient-to-r from-yellow-950/60 to-amber-950/40 border border-yellow-700/40 rounded-xl px-4 py-3.5">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-yellow-800/50 flex-shrink-0 mt-0.5">
                <Trophy size={16} className="text-yellow-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-1">Top Win</p>
                <p className="text-sm text-amber-100 leading-relaxed">
                  <span className="mr-1.5">{topWin.icon}</span>
                  {topWin.text}
                </p>
              </div>
            </div>
          </div>

          {/* Quick stats grid */}
          {statCards.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {statCards.map((s, i) => (
                <div key={i} className="bg-slate-800/70 border border-slate-700/60 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-base leading-none">{s.emoji}</span>
                    <span className="text-xs text-slate-500 font-medium">{s.label}</span>
                  </div>
                  <div className={`text-xl font-bold tabular-nums ${s.color}`}>{s.value}</div>
                  {s.sub && <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>}
                </div>
              ))}
            </div>
          )}

          {/* More wins — under-budget categories */}
          {stats.underBudgetCats.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2.5">
                🎉 Under Budget
              </p>
              <div className="space-y-2">
                {stats.underBudgetCats.slice(0, 4).map(c => (
                  <div key={c.name} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-300">{c.name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-20 bg-slate-700 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-1.5 rounded-full bg-emerald-500"
                          style={{ width: `${Math.min(c.pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-emerald-400 font-medium w-16 text-right">
                        {fmt(c.saved)} under
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opportunity (positive framing) */}
          {opportunity && (
            <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl px-4 py-3.5">
              <div className="flex items-start gap-2.5">
                <TrendingDown size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-blue-400 mb-1">💡 One opportunity for next month</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{opportunity}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {stats.txnCount === 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-slate-400">No transactions logged last month.</p>
              <p className="text-xs text-slate-600 mt-1">Start logging this month to see your wins here next time.</p>
            </div>
          )}

        </div>

        {/* ── Footer actions ── */}
        <div className="px-6 pb-5 pt-2 flex items-center gap-3 flex-shrink-0 border-t border-slate-800">
          <button
            onClick={handlePrint}
            disabled={printing}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-2 rounded-lg transition-colors"
          >
            <Printer size={12} /> Save PDF
          </button>
          <button
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"
          >
            Keep it up <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
